import { EXCLUDED_SECTOR_IDS } from '@/creditflowresearch/application/Deal/constants';
import * as FILTER_TYPES from '@/creditflowresearch/application/PageFiltersState/constants';
import { IFilters, IFiltersPayload, ITopParentsPayload } from '@/creditflowresearch/application/TopCompanies/interfaces';
import { getFiltersByPairs } from '@/creditflowresearch/application/actions/profile/helpers/getFiltersByPairs';
import Market from '@/creditflowresearch/domain/Market';
import Segment from '@/creditflowresearch/domain/Segment';
import { formatInTimeZone } from 'date-fns-tz';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';

const generateFileName = (args: {
  productIds: string[],
  products: { id: string, abbreviation: string }[],
  regionIds: string[],
  regions: { id: string, abbreviation: string }[],
  markets: string[],
  segments: string[],
  period: string,
  timeZone: ITimeZone,
  sectorName?: string,
  industryName?: string,
  parentName?: string | null,
}): string => {
  const {
    productIds,
    products,
    regionIds,
    regions,
    markets = [],
    segments = [],
    period,
    timeZone,
    sectorName,
    industryName,
    parentName,
  } = args;

  const fileName = [];

  let date = `${ formatInTimeZone(new Date(), timeZone.timeZone, 'yyyy-MM-dd HHmm') } ${ timeZone.abbr }`;
  let name = `Top ${ parentName ? 'Issuers' : 'Parents' }, Rating Agencies, Underwriters`;
  let productAbbreviation = [];
  let regionAbbreviation = [];

  for (let product of products) {
    productIds.forEach((id) => {
      if (id === product.id) {
        productAbbreviation.push(product.abbreviation);
      }
    });
  }

  for (let region of regions) {
    regionIds.forEach((id) => {
      if (id === region.id) {
        regionAbbreviation.push(region.abbreviation);
      }
    });
  }

  fileName.push(name);

  if (parentName) {
    fileName.push(parentName);
  }

  if (sectorName) {
    fileName.push(sectorName);
  }
  if (industryName) {
    fileName.push(industryName);
  }

  if (regionAbbreviation.length > 0) fileName.push(regionAbbreviation.join(','));
  if (productAbbreviation.length > 0) fileName.push(productAbbreviation.join(','));
  if (markets.length > 0) fileName.push(markets.join(','));
  if (segments.length > 0) fileName.push(segments.join(','));
  fileName.push(period === 'TTM' ? 'past 12 months' : period);
  fileName.push(date);

  return fileName.join(' ');
};

export const getFilters = ({
  profileType,
  isConsumerOverview,
  productIds,
  regionIds,
  industry,
  sector,
  includedParentId,
  sectorsList,
  industriesList,
  mapping,
  leagueType,
  ...additionalFilters
}: IFilters) => {
  let filters = {
    productIds,
    regionIds,
    sector: null,
    industry: null,
    includedParentId: null,
    isOverview: (!industry && !sector && !leagueType),
    isConsumerOverview,
  };

  if (sector) {
    filters.sector = sectorsList[sector];
  }

  if (industry) {
    filters.industry = industriesList[industry];
  }

  if (includedParentId) {
    filters.includedParentId = includedParentId;
  }

  const profileFilters = getFiltersByPairs({
    mapping,
    profileType,
    selectedProducts: filters.productIds,
    selectedRegions: filters.regionIds,
  });

  // @todo rewrite getTopCompaniesBy to enum type
  const sponsorFilters = includedParentId ? {
    getDealsBy: FILTER_TYPES.COMPANY,
    getTopCompaniesBy: FILTER_TYPES.PARENT,
    getChartBy: FILTER_TYPES.PARENT,
    targetViewIds: includedParentId,
    includedCompanyRole: FILTER_TYPES.PARENT,
  } : {};

  const scorecardFilters = leagueType ? {
    getDealsBy: FILTER_TYPES.COMPANY,
  } : {};

  return {
    profileType,
    hasEitherPricingOrAnnouncedDate: true,
    ...filters,
    ...profileFilters,
    ...sponsorFilters,
    ...scorecardFilters,
    ...additionalFilters,
  };
};

const preparePagePayload = (pageFilters, payload) => {
  let filters: Partial<IFiltersPayload> = {};

  if (
      pageFilters.isOverview
      && pageFilters.isConsumerOverview
      && pageFilters.getTopCompaniesBy === FILTER_TYPES.SECTOR
  ) {
    filters.excludedSectorIds = EXCLUDED_SECTOR_IDS;
  }

  if (pageFilters.includedParentId) {
    filters.includedParentId = pageFilters.includedParentId;
  }

  if (pageFilters.sector) {
    filters.includedSectorId = pageFilters.sector.sectorId;

    if (pageFilters.sector.subsectorId) {
      filters.includedSubsectorId = pageFilters.sector.subsectorId;
    }
  }

  if (pageFilters.industry) {
    filters.includedIndustryId = pageFilters.industry.industryId;

    if (pageFilters.industry.subindustryId) {
      filters.includedSubindustryId = pageFilters.industry.subindustryId;
    }
  }

  return { ...payload, filters: { ...payload.filters, ...filters } };
};

export const prepareTopPeriodsPayload = (pageFilters) => {
  let payload = {
    groupBy: pageFilters.getTopCompaniesBy,
    filters: {
      includedProductIds: pageFilters.productIds,
      includedRegionIds: pageFilters.regionIds,
    },
  };

  return preparePagePayload(pageFilters, payload);
};

export const prepareTopCompanyPayload = ({ pageFilters, perPage, period, dateFrom, dateTo, includedCompanyRole }) => {
  let payload: Partial<ITopParentsPayload> = {
    perPage,
    groupBy: pageFilters.getTopCompaniesBy,
    period,
    filters: {
      includedProductIds: pageFilters.productIds,
      includedRegionIds: pageFilters.regionIds,
      includedCompanyRole,
      dateFrom,
      dateTo,
    },
  };

  return preparePagePayload(pageFilters, payload);
};

export const prepareExportToExcelPayload = ({
  pageFilters, selectedPeriod, products, regions, parentName, timeZone,
}: {
  pageFilters: any,
  selectedPeriod: any,
  products: any,
  regions: any,
  parentName?: string | null,
  timeZone: ITimeZone,
}) => {
  const {
    includedProductIds,
    includedRegionIds,
    sector,
    industry,
    includedMarketIds,
    includedSegmentIds,
    getTopCompaniesBy,
  } = pageFilters;
  const { value, label, dateFrom, dateTo } = selectedPeriod;
  const fileName = generateFileName({
    productIds: includedProductIds,
    products,
    regionIds: includedRegionIds,
    regions,
    period: label,
    timeZone,
    sectorName: sector ? sector.metaTitleName : '',
    industryName: industry ? industry.metaTitleName : '',
    markets: Market.getAbbreviations(includedMarketIds),
    segments: Segment.getAbbreviations(includedSegmentIds),
    parentName,
  });

  const payload: Partial<ITopParentsPayload> = {
    groupBy: getTopCompaniesBy,
    period: value,
    fileName,
    filters: {
      excludedSectorIds: [],
      includedProductIds,
      includedRegionIds,
      includedMarketIds,
      includedSegmentIds,
      // todo remove conditions when top companies export will use string
      dateFrom: typeof dateFrom === 'string' ? new Date(dateFrom).getTime() / 1000 : dateFrom,
      dateTo: typeof dateTo === 'string' ? new Date(dateTo).getTime() / 1000 : dateTo,
    },
  };

  return preparePagePayload(pageFilters, payload);
};
